import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AppSettingsService, XSightHttpService, isPermissionError } from '@nx-fe/services';
import {
  SabhaAuthenticationHandlerService,
  SabhaCallService,
  SabhaConnectionProviderService,
  SabhaCustomPresenceStatus,
  SabhaPresenceService,
  SabhaRoomType,
  SabhaUserEntity,
  UserRepository,
  inCallCustomStatusOverride,
  initiWithClearState,
} from '@soti/sabha-components';
import { Observable, firstValueFrom, from, of, throwError } from 'rxjs';
import { catchError, take } from 'rxjs/operators';
import { CHAT_ROOM_ENDPOINT } from './constants';
import { ParticipantAlreadyInSessionError, UnsupportedDeviceError, isDeviceMissingError } from './errors/device-errors';
import { CreateRoomRequest } from './models/create-room-request';
import { CreateRoomResponse } from './models/create-room-response';
import { SabhaState } from './store/reducers';
import { Store } from '@ngrx/store';

export interface ISabhaConfig {
  baseUri: string;
}

@Injectable({ providedIn: 'root' })
export class SabhaService {
  public constructor(
    private http: XSightHttpService,
    private store: Store<SabhaState>,
    private appSettings: AppSettingsService,
    private sabhaAuthenticationService: SabhaAuthenticationHandlerService,
    private sabhaConnectionService: SabhaConnectionProviderService,
    private sabhaPresenceService: SabhaPresenceService,
    private sabhaCallService: SabhaCallService,
    private userRepository: UserRepository
  ) {
    this.sabhaPresenceService.addPresenceStatusHandler(initiWithClearState());
    this.sabhaPresenceService.addPresenceStatusHandler(inCallCustomStatusOverride(this.sabhaCallService));
  }

  public configureAndCheckConnection() {
    this.sabhaConnectionService.configure({
      origin: window.location.origin + '/sotixsight/chat/',
    });
    return of(true);
  }

  public logout(): void {
    this.sabhaAuthenticationService.deauthenticate();
  }

  public setPresenceState(status: SabhaCustomPresenceStatus): void {
    this.sabhaPresenceService.setStatus(status);
  }

  public getUserBySabhaId(sabhaUserId: string): Observable<SabhaUserEntity> {
    return from(this.userRepository.getUserByIdAsync(sabhaUserId));
  }

  public getLocalPresenceStatus() {
    return this.sabhaPresenceService.currentUserStatus$;
  }

  public getCurrentUser(): Observable<SabhaUserEntity> {
    return from(this.userRepository.localUser);
  }

  public switchToCall(roomId: string) {
    this.sabhaCallService
      .getCallByRoomId$(roomId)
      .pipe(take(1))
      .subscribe({
        next: call => {
          if (call) {
            this.sabhaCallService.switchToCall(call);
          }
        },
      });
  }

  // TODO: re-enable this when USER_GET_BY_IDS is refactored and we
  // can get online status
  public canCallDevice$(mcDeviceId?: string) {
    return of(true);
  }
  // public canCallDevice$(mcDeviceId?: string): Observable<boolean> {
  //   // Ensure we are connected to signaling channel
  //   // and device is online
  //   return this.userRepository.users$.pipe(
  //     map(participants => {
  //       const clientConnected = this.signalingChannel.connectionStatus === ConnectionStatus.Connected;
  //       const participant = participants.find(participant => participant.deviceId === mcDeviceId);
  //       return clientConnected && participant && participant.online;
  //   }));
  // }

  // TODO: re-enable this when USER_GET_BY_IDS is refactored and we
  // can get online status
  public canCallParticipant$(sabhaUserId?: string) {
    return of(true);
  }
  // public canCallParticipant$(sabhaUserId?: string): Observable<boolean> {
  //   return this.userRepository.users$.pipe(
  //     map(participants => {
  //       const clientConnected = this.signalingChannel.connectionStatus === ConnectionStatus.Connected;
  //       const participant = participants.find(participant => participant.userId === sabhaUserId);
  //       return clientConnected && participant && participant.online;
  //   }));
  // }

  public getOrCreateRoom(
    roomName: string,
    initialRoomType: SabhaRoomType,
    mcDeviceId: string
  ): Promise<CreateRoomResponse> {
    const request = <CreateRoomRequest>{
      roomName: roomName,
      deviceId: mcDeviceId,
      initialRoomType: initialRoomType,
    };
    return firstValueFrom(
      this.http.post<CreateRoomResponse>(`${this.appSettings.chatServiceUrl}${CHAT_ROOM_ENDPOINT}`, request).pipe(
        catchError(error => {
          if (error instanceof HttpErrorResponse) {
            const err = error.error;
            if (error.status === 409) {
              return throwError(
                () =>
                  new ParticipantAlreadyInSessionError(
                    err.deviceName,
                    err.userDisplayName,
                    err.sabhaDeviceId,
                    err.sabhaParticipantId
                  )
              );
            } else if (error.status === 403 && isPermissionError(err)) {
              return throwError(() => err);
            } else if (error.status === 422) {
              if (isDeviceMissingError(err)) {
                return throwError(() => err);
              }
              return throwError(() => new UnsupportedDeviceError());
            }
          }
          return throwError(() => error);
        })
      )
    );
  }
}
